import {
    EuiBadge,
    EuiButton,
    EuiFieldNumber,
    EuiFieldText,
    EuiFlexGroup,
    EuiFlexItem,
    EuiForm,
    EuiFormRow,
    EuiSelect,
    EuiSpacer,
    EuiSwitch,
    EuiText,
    EuiToolTip,
} from "@elastic/eui";
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { ActionLabel, JsonPrice, Price } from "utils/types";

import { useEditShopToPrice } from "../api/react-query/mutations/shop-to-prices";
import { UserContext } from "../App";
import { ShopContext } from "../utils/Shop";

export function ColumnPriceForm({
    usedPrice,
    currentLabel,
    tablePriceValue,
    labelSuffix,
    setShowModal,
    handlePriceSubmit,
    setLabelSuffix,
    cannabisJsonPrices,
    edibleJsonPrices,
    preRolledJsonPrices,
    setCannabisJsonPrices,
    setEdibleJsonPrices,
    setPreRolledJsonPrices,
    setActionLabels,
}: {
    usedPrice: Price;
    currentLabel: string | undefined;
    tablePriceValue: number | undefined;
    setShowModal: Dispatch<SetStateAction<boolean>>;
    labelSuffix: string;
    handlePriceSubmit: () => void;
    setLabelSuffix: Dispatch<SetStateAction<string>>;
    setCannabisJsonPrices: Dispatch<SetStateAction<JsonPrice[]>>;
    setEdibleJsonPrices: Dispatch<SetStateAction<JsonPrice[]>>;
    setPreRolledJsonPrices: Dispatch<SetStateAction<JsonPrice[]>>;
    setActionLabels: Dispatch<SetStateAction<ActionLabel[]>>;
    cannabisJsonPrices: JsonPrice[];
    edibleJsonPrices: JsonPrice[];
    preRolledJsonPrices: JsonPrice[];
}) {
    const [readyToSubmit, setReadyToSubmit] = useState(false);
    const { profile } = useContext(UserContext);
    const canEdit = profile.is_superuser || (profile as any).roles?.some((r: any) => r.name === "operator");
    const hasLessThanTwoPrices =
        (cannabisJsonPrices?.length ?? 0) + (edibleJsonPrices?.length ?? 0) + (preRolledJsonPrices?.length ?? 0) < 2;
    const isDeleteButtonDisabled = hasLessThanTwoPrices || !canEdit;
    const { mutate: toggleLabel } = useEditShopToPrice();
    const { shopId } = useContext(ShopContext);

    const [price, setPrice] = useState<JsonPrice>(
        currentLabel && tablePriceValue
            ? {
                  quantity: Number(currentLabel.split("g")[0].split("j")[0].split("st")[0].split(" ")[0]),
                  price: tablePriceValue,
                  label: currentLabel,
                  active: true,
              }
            : {
                  quantity: 0,
                  price: 0,
                  label: "0g",
                  active: true,
              }
    );

    const hasActionLabel = usedPrice.action_labels?.some((actionLabel) => actionLabel.label === currentLabel) === true;
    const [enableDiscount, setEnableDiscount] = useState(hasActionLabel);
    const [discountPrice, setDiscountPrice] = useState(
        currentLabel && tablePriceValue
            ? {
                  price: hasActionLabel
                      ? usedPrice.action_labels.find((al) => al.label === currentLabel)!.price
                      : price.price,
                  label: currentLabel,
              }
            : {
                  price: price.price,
                  label: price.label,
              }
    );

    const selectedSuffix = currentLabel ? price.label.match(/^([\d.]+)(\D+)$/)?.[2] : labelSuffix;

    const [ogPrice] = useState<JsonPrice>(
        currentLabel && tablePriceValue
            ? {
                  quantity: Number(currentLabel.split("g")[0].split("j")[0].split("st")[0].split(" ")[0]),
                  price: tablePriceValue,
                  label: currentLabel,
                  active: true,
              }
            : {
                  quantity: 0,
                  price: 0,
                  label: "0g",
                  active: true,
              }
    );

    const options = [
        {
            value: "g",
            text: "Cannabis",
        },
        {
            value: "st",
            text: "Edible",
        },
        {
            value: "j",
            text: "Pre-Rolled Joints",
        },
    ];

    useEffect(() => {
        if (readyToSubmit) {
            handlePriceSubmit();
        }
    }, [readyToSubmit, handlePriceSubmit]);

    const setJsonPrice = () => {
        const jsonPrices: JsonPrice[] = determineJsonPrices()!;
        const setJsonPrices: Dispatch<SetStateAction<JsonPrice[]>> = determineSetJsonPrices()!;
        const prices: JsonPrice[] = [...jsonPrices];
        const jsonPrice = prices.find((p) => p.label.toString() === ogPrice.label.toString());

        if (jsonPrice) {
            prices[prices.indexOf(jsonPrice)] = price;
            setJsonPrices(prices);
            setReadyToSubmit(true);
        }
    };

    const addJsonPrice = () => {
        const jsonPrices: JsonPrice[] = determineJsonPrices()! ?? [];
        console.log("JSON PRICES", jsonPrices);
        const setJsonPrices: Dispatch<SetStateAction<JsonPrice[]>> = determineSetJsonPrices()!;

        const prices: JsonPrice[] = [...jsonPrices];
        prices.push(price);
        setJsonPrices(prices);
        setReadyToSubmit(true);
    };

    const removeJsonPrice = () => {
        const jsonPrices: JsonPrice[] = determineJsonPrices()!;
        const setJsonPrices: Dispatch<SetStateAction<JsonPrice[]>> = determineSetJsonPrices()!;

        let newActionLabels = hasActionLabel ? [...usedPrice.action_labels] : [];
        newActionLabels = newActionLabels.filter((al) => al.label !== currentLabel);
        setActionLabels(newActionLabels);

        if (jsonPrices.length > 0) {
            const prices: JsonPrice[] = [...jsonPrices];
            const jsonPrice = prices.find((p) => p.label.toString() === price.label.toString());
            if (jsonPrice) {
                delete prices[prices.indexOf(jsonPrice)];
                setJsonPrices(prices.filter((p) => p !== null));
                setReadyToSubmit(true);
            }
        }
    };

    function determineJsonPrices() {
        switch (selectedSuffix) {
            case "g":
                return cannabisJsonPrices;
            case "st":
                return edibleJsonPrices;
            case "j":
                return preRolledJsonPrices;
            default:
                break;
        }
    }

    function determineSetJsonPrices() {
        switch (selectedSuffix) {
            case "g":
                return setCannabisJsonPrices;
            case "st":
                return setEdibleJsonPrices;
            case "j":
                return setPreRolledJsonPrices;
            default:
                break;
        }
    }

    const hasDeactivatedLabel = currentLabel && usedPrice.deactivated_labels?.includes(currentLabel);

    const handleToggleAction = (disable: boolean) => {
        if (discountPrice.price >= price.price) {
            console.error("Discounted price cannot be higher or equal to normal price");
            return;
        }

        if (usedPrice.kind_id && currentLabel) {
            let newActionLabels = usedPrice.action_labels != null ? [...usedPrice.action_labels] : [];
            const index = newActionLabels.findIndex((al) => al.label === currentLabel);

            if (disable) {
                if (index !== -1) {
                    newActionLabels.splice(index, 1);
                }
            } else {
                if (index !== -1) {
                    newActionLabels[index] = discountPrice;
                } else {
                    newActionLabels.push(discountPrice);
                }
            }

            setActionLabels(newActionLabels);

            toggleLabel({
                id: usedPrice.id,
                data: {
                    active: usedPrice.active,
                    new: usedPrice.new,
                    price_id: usedPrice.price_id,
                    shop_id: shopId,
                    category_id: usedPrice.category_id,
                    kind_id: usedPrice.kind_id,
                    deactivated_labels: [],
                    action_labels: newActionLabels,
                },
            });
        } else {
            console.error("No kind_id provided");
        }
    };

    const ActiveBadge = () => {
        return (
            <div style={{ display: "flex", marginTop: 8, alignItems: "center" }}>
                <div style={{ marginRight: 8 }}>Is Actief ? -</div>
                {hasDeactivatedLabel ? (
                    <EuiBadge iconType="cross" color="danger">
                        Nee ({currentLabel})
                    </EuiBadge>
                ) : (
                    <EuiBadge iconType="check" color="success">
                        Ja ({currentLabel})
                    </EuiBadge>
                )}
            </div>
        );
    };

    const parseInputNum = (raw: string): number => {
        const num = parseFloat(raw);
        return isNaN(num) ? 0 : num;
    };

    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrice({ ...price, price: parseInputNum(e.target.value) });
    };

    const handleDiscountPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDiscountPrice({ ...discountPrice, price: parseInputNum(e.target.value) });
    };

    const handleZeroKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.currentTarget.value === "0" && /^[0-9]$/.test(e.key)) {
            e.currentTarget.select();
        }
    };

    const handleEnableDiscount = (checked: boolean) => {
        setEnableDiscount(checked);
        if (!checked && usedPrice.action_labels.length > 0) {
            handleToggleAction(true);
        }
    };

    return (
        <EuiForm>
            {currentLabel && (
                <EuiFormRow style={{ marginTop: -10 }}>
                    <ActiveBadge />
                </EuiFormRow>
            )}
            <EuiFormRow
                label="Type"
                labelAppend={
                    <EuiText size="s">Selecteer het type product (dit bepaalt de eenheid van de hoeveelheid).</EuiText>
                }
                id="select-price"
                fullWidth
                isDisabled={!!currentLabel}
            >
                <EuiSelect
                    options={options}
                    value={selectedSuffix}
                    onChange={(e) => {
                        const newSuffix = e.target.value;
                        setLabelSuffix(newSuffix);
                        setPrice({ ...price, label: `${price.quantity}${newSuffix}` });
                    }}
                />
            </EuiFormRow>
            <EuiFormRow
                label="Hoeveelheid"
                labelAppend={<EuiText size="s">Vul de hoeveelheid in (in grammen, joints of aantal stuks).</EuiText>}
                id="select-price"
                fullWidth
            >
                <EuiFieldNumber
                    step={0.5}
                    value={price.quantity}
                    fullWidth
                    onFocus={(e) => e.currentTarget.select()}
                    onKeyDown={handleZeroKeyDown}
                    onChange={(e) => {
                        const qty = parseInputNum(e.target.value);
                        setPrice({ ...price, quantity: qty, label: `${qty}${selectedSuffix ?? labelSuffix}` });
                    }}
                />
            </EuiFormRow>
            <EuiFlexGroup alignItems="center">
                <EuiFlexItem>
                    <EuiFormRow
                        label="Prijs"
                        labelAppend={<EuiText size="s">Vul de prijs in (in euro).</EuiText>}
                        id="select-price"
                        fullWidth
                    >
                        <EuiFieldNumber
                            step={0.25}
                            value={price.price}
                            fullWidth
                            onFocus={(e) => e.currentTarget.select()}
                            onKeyDown={handleZeroKeyDown}
                            onChange={handlePriceChange}
                        />
                    </EuiFormRow>
                </EuiFlexItem>
                <EuiSwitch
                    checked={enableDiscount}
                    label="Actie?"
                    onChange={(e) => handleEnableDiscount(e.target.checked)}
                />
            </EuiFlexGroup>

            {enableDiscount && (
                <EuiFlexGroup alignItems="center">
                    <EuiFlexItem>
                        <EuiFormRow
                            label="Prijs met Korting"
                            labelAppend={<EuiText size="s">Vul de afgeprijsde prijs in (in euro).</EuiText>}
                            id="select-price"
                            fullWidth
                        >
                            <EuiFieldNumber
                                step={0.25}
                                value={discountPrice.price}
                                fullWidth
                                onFocus={(e) => e.currentTarget.select()}
                                onKeyDown={handleZeroKeyDown}
                                onChange={handleDiscountPriceChange}
                            />
                        </EuiFormRow>
                    </EuiFlexItem>
                    <EuiButton onClick={() => handleToggleAction(false)}>Actie Toepassen</EuiButton>
                </EuiFlexGroup>
            )}
            <EuiFormRow label="Label" id="select-price" fullWidth>
                <EuiFieldText size={"s"} value={price.quantity + selectedSuffix ?? price.label} fullWidth disabled />
            </EuiFormRow>
            <EuiText size="s">Als u een prijs toevoegt of wijzigt, worden alle prijzen ingeschakeld.</EuiText>
            <EuiSpacer />
            <EuiFlexGroup direction="row" justifyContent="spaceBetween">
                {currentLabel ? (
                    <>
                        <EuiToolTip
                            position="top"
                            content={
                                // isDeleteButtonDisabled
                                "Je moet minstens 2 prijzen hebben."
                                // : isAdmin
                                // ? ""
                                // : "Alleen admins kunnen prijzen verwijderen"
                            }
                        >
                            <EuiButton
                                iconType={"trash"}
                                color="danger"
                                disabled={isDeleteButtonDisabled}
                                size={"s"}
                                onClick={() => removeJsonPrice()}
                            >
                                Verwijder
                            </EuiButton>
                        </EuiToolTip>
                        <EuiButton iconType={"save"} size={"s"} disabled={!canEdit} onClick={() => setJsonPrice()}>
                            Bewaar
                        </EuiButton>
                    </>
                ) : (
                    <EuiButton iconType={"plus"} fill size={"s"} disabled={!canEdit} onClick={() => addJsonPrice()}>
                        Voeg toe
                    </EuiButton>
                )}
                <EuiButton size={"s"} onClick={() => setShowModal(false)}>
                    Annuleer
                </EuiButton>
            </EuiFlexGroup>
        </EuiForm>
    );
}
